import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Locale, resources, TranslationKey } from "./translations";

const STORAGE_KEY = "appLocale";
const DEFAULT_LOCALE: Locale = "en-PH";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const isLocale = (value: string | null): value is Locale => {
  return value === "en-PH" || value === "zh-TW";
};

const formatMessage = (
  message: string,
  values?: Record<string, string | number>
) => {
  if (!values) return message;

  return Object.entries(values).reduce((result, [key, value]) => {
    return result.split(`{{${key}}}`).join(String(value));
  }, message);
};

const getInitialLocale = (): Locale => {
  const storedLocale = localStorage.getItem(STORAGE_KEY);
  if (isLocale(storedLocale)) return storedLocale;

  const browserLanguage = navigator.language;
  if (browserLanguage === "zh-TW") return "zh-TW";

  return DEFAULT_LOCALE;
};

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = (nextLocale: Locale) => {
    setLocaleState(nextLocale);
    localStorage.setItem(STORAGE_KEY, nextLocale);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const t = (
      key: TranslationKey,
      values?: Record<string, string | number>
    ): string => {
      const message = resources[locale][key] ?? resources[DEFAULT_LOCALE][key];
      return formatMessage(message, values);
    };

    return { locale, setLocale, t };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }

  return context;
};

export type { Locale, TranslationKey };
